/* AthletePlatformLinks — Phase 1 extension
 * ─────────────────────────────────────────────────────────────────────
 * Branded outbound buttons for athlete-first platforms:
 *   • Hudl      → the college recruiting film standard.
 *   • MaxPreps  → verified prep stats & schedules.
 *   • Opendorse → NIL deal availability & booking.
 *
 * These sit *below* the tiny social icon row because they deserve
 * heavier visual weight — recruiters and brands click these first.
 */
import { Film, BarChart3, Handshake } from 'lucide-react';

const PLATFORMS = [
  {
    key: 'hudl',
    field: 'hudl_url',
    label: 'Hudl',
    caption: 'Game film',
    icon: Film,
    color: '#FF6600',     // Hudl orange
  },
  {
    key: 'maxpreps',
    field: 'maxpreps_url',
    label: 'MaxPreps',
    caption: 'Verified stats',
    icon: BarChart3,
    color: '#E31C25',     // MaxPreps red
  },
  {
    key: 'opendorse',
    field: 'opendorse_url',
    label: 'Opendorse',
    caption: 'NIL deals',
    icon: Handshake,
    color: '#00A9E0',     // Opendorse cyan-blue
  },
];

const normalize = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

import { Globe } from 'lucide-react';
import { siteIconUrl } from '../../utils/siteIcon';

const AthletePlatformLinks = ({ athlete, className = '' }) => {
  if (!athlete) return null;
  const active = PLATFORMS
    .map((p) => ({ ...p, url: normalize(athlete[p.field]) }))
    .filter((p) => !!p.url);
  // iter586 · Custom-titled platforms — MOGL / TeamBuildr / any URL
  // the athlete adds themselves. Rendered right next to Hudl /
  // MaxPreps / Opendorse so the whole row reads as one link stack.
  const customPlatforms = Array.isArray(athlete.custom_platforms)
    ? athlete.custom_platforms
        .map((p) => {
          const url = normalize(p?.url);
          const title = (p?.title || '').trim();
          if (!url || !title) return null;
          let host = '';
          try { host = new URL(url).hostname.replace(/^www\./, ''); } catch { /* fine */ }
          return { title, url, host };
        })
        .filter(Boolean)
    : [];
  if (active.length === 0 && customPlatforms.length === 0) return null;
  return (
    <div
      className={`mt-3 flex flex-wrap items-center gap-2 ${className}`}
      data-testid="athlete-platform-links"
    >
      {active.map(({ key, label, caption, icon: Icon, color, url }) => (
        <a
          key={key}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`platform-link-${key}`}
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ring-white/12 bg-white/[0.05] hover:bg-white/[0.09] transition"
          style={{ boxShadow: `inset 0 0 0 1px ${color}22` }}
          aria-label={`${label} — ${caption}`}
        >
          <span
            className="inline-flex items-center justify-center w-5 h-5 rounded-full"
            style={{ background: `${color}22`, color }}
          >
            <Icon className="w-3 h-3" />
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/95">{label}</span>
            <span className="text-[9px] uppercase tracking-widest text-white/45">{caption}</span>
          </span>
        </a>
      ))}
      {customPlatforms.map((c, i) => (
        <a
          key={`custom-${i}-${c.url}`}
          href={c.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`platform-link-custom-${i}`}
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ring-white/12 bg-white/[0.05] hover:bg-white/[0.09] transition"
          aria-label={`${c.title} — ${c.host || 'External link'}`}
        >
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/[0.06] overflow-hidden">
            {c.host ? (
              <img
                src={siteIconUrl(c.url, 64)}
                alt=""
                className="w-4 h-4 object-contain"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <Globe className="w-3 h-3 text-white/70" />
            )}
          </span>
          <span className="flex flex-col leading-tight">
            <span className="text-[11px] font-bold uppercase tracking-widest text-white/95">{c.title}</span>
            <span className="text-[9px] uppercase tracking-widest text-white/45 truncate max-w-[110px]">{c.host || 'Custom'}</span>
          </span>
        </a>
      ))}
    </div>
  );
};

export default AthletePlatformLinks;
